/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 08/09/2025 - 21:16:07
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 08/09/2025
    * - Author          : 
    * - Modification    : 
**/
import { auth } from '../firebase';

// Response interfaces
interface LocationResponse {
  success: boolean;
  blocked: boolean;
  message?: string;
  sessionId?: string;
}

interface SessionResponse {
  success: boolean;
}

const TRACK_LOCATION_URL = 'https://stripe.pv-glarecheck.com/track-location';
const UPDATE_SESSION_URL = 'https://stripe.pv-glarecheck.com/track-location/update-session';
const END_SESSION_URL = 'https://stripe.pv-glarecheck.com/track-location/end-session';

class LocationAPI {
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private activityTimeout: NodeJS.Timeout | null = null;
  private readonly idleThreshold: number = 10 * 60 * 1000;
  private readonly heartbeatFrequency: number = 5 * 60 * 1000;

  async trackUserLocation(): Promise<LocationResponse> {
    try {
      const firebaseToken = await auth.currentUser?.getIdToken();
      if (!firebaseToken) return { success: false, blocked: false };
      const sessionId = localStorage.getItem('sessionId');

      const response = await fetch(TRACK_LOCATION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          token: firebaseToken,
          sessionId,
          userAgent: navigator.userAgent,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height
        })
      });
      
      if (response.status === 403) {
        const data = await response.json() as LocationResponse;
        console.log('Location blocked:', data.message);
        return { 
          success: false, 
          blocked: true, 
          message: data.message || "This service is not available in your region" 
        };
      }
      
      const data = await response.json() as LocationResponse;
      console.log('Location tracking response:', data);
      
      if (data.sessionId) {
        localStorage.setItem('sessionId', data.sessionId);
        console.log('Session ID stored in localStorage:', data.sessionId);
      }
      if (!data.blocked) {
        this.startActivityTracking();
      }
      
      return { 
        success: true, 
        blocked: false,
        sessionId: data.sessionId
      };
    } catch (error) {
      console.error('Location tracking error:', error);
      return { success: true, blocked: false };
    }
  }

  async updateSessionActivity(): Promise<SessionResponse> {
    try {
      const firebaseToken = await auth.currentUser?.getIdToken();
      if (!firebaseToken) return { success: false };
      
      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) return { success: false };
      
      const response = await fetch(UPDATE_SESSION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          token: firebaseToken,
          sessionId,
          currentPage: window.location.pathname
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Session heartbeat error:', error);
      return { success: false };
    }
  }
  

  async endSession(): Promise<SessionResponse> {
    try {
      const firebaseToken = await auth.currentUser?.getIdToken();
      if (!firebaseToken) return { success: false };
      
      const sessionId = localStorage.getItem('sessionId');
      if (!sessionId) return { success: false };
      
      console.log('Ending session:', sessionId);
      
      const response = await fetch(END_SESSION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          token: firebaseToken,
          sessionId
        })
      });
      

      localStorage.removeItem('sessionId');
      

      this.stopActivityTracking();
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return { success: true };
    } catch (error) {
      console.error('Session end error:', error);
      localStorage.removeItem('sessionId');
      return { success: false };
    }
  }
  

  startActivityTracking(): void {
    this.stopActivityTracking();
    
    console.log('Starting activity tracking');
    
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(eventType => {
      window.addEventListener(eventType, this.resetActivityTimer);
    });
    
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    
    this.sendHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.heartbeatFrequency);
    
    this.resetActivityTimer();
  }

  stopActivityTracking(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
      this.activityTimeout = null;
    }
    
    ['mousedown', 'keydown', 'touchstart', 'scroll'].forEach(eventType => {
      window.removeEventListener(eventType, this.resetActivityTimer);
    });
    
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
  }

  resetActivityTimer = (): void => {
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }

    this.activityTimeout = setTimeout(() => {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    }, this.idleThreshold);
    
    if (!this.heartbeatInterval) {
      this.sendHeartbeat();
      this.heartbeatInterval = setInterval(() => {
        this.sendHeartbeat();
      }, this.heartbeatFrequency);
    }
  };
  

  private sendHeartbeat(): void {
    this.updateSessionActivity().catch(err => {
      console.error('Heartbeat error:', err);
    });
  }

  handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      if (auth.currentUser && localStorage.getItem('sessionId')) {
        console.log('Tab became visible, resuming activity tracking');
        this.resetActivityTimer();
        if (!this.heartbeatInterval) {
          this.sendHeartbeat();
          this.heartbeatInterval = setInterval(() => {
            this.sendHeartbeat();
          }, this.heartbeatFrequency);
        }
      }
    } else {
      console.log('Tab became hidden, pausing heartbeat');
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    }
  };
  
  handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    const sessionId = localStorage.getItem('sessionId');
    const token = localStorage.getItem('firebaseToken');
    
    if (sessionId && token) {
      console.log('Page unloading, sending end-session beacon');
      
      const blob = new Blob([JSON.stringify({ token, sessionId })], 
        { type: 'application/json' });
      
      navigator.sendBeacon(END_SESSION_URL, blob);
    }
  };
}

export const locationapi = new LocationAPI();

if (auth.currentUser && localStorage.getItem('sessionId')) {
  console.log('User already logged in with active session, starting tracking');
  locationapi.startActivityTracking();
}